import { forwardRef, Ref, useState } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import type { AccordionSize } from '@opendoor/tokens-studio/dist/bricks-next/types/Components';
import { Color } from '@opendoor/tokens-studio/dist/bricks-next/types/Novo';
import { Accordion as AccordionTamagui } from '@tamagui/accordion';
import { TamaguiElement, useProps, WithMediaProps } from '@tamagui/core';

import { ChevronDown } from '../../__generated__/icons';
import { AnalyticsProps, ComponentWithViewTracking } from '../../helpers/analytics';
import { useObservabilitySafe } from '../../helpers/useObservabilitySafe';
import { Box } from '../Box';
import { Text } from '../Text';
import { HeightAnimator } from './HeightAnimator';

const { accordion: accordionTokens } = tokens;

type VariantProps = 'regular' | 'medium';

type AccordionItem = {
  /**
   * Provide the description
   */
  description: React.ReactNode;
  /**
   * Provide the title
   */
  title: string;
  /**
   * Optional text displayed above the title
   */
  eyebrowText?: string;
  /**
   * Optional title suffix
   * This does not show if the accordion is full width
   */
  suffix?: React.ReactNode;
};

// Define AccordionProps
export type AccordionProps = Partial<AnalyticsProps> & {
  /**
   * Threshold for impression tracking. 0.5 means 50% of the box must be in view to trigger impression
   * Default: 1 (100%)
   */
  inViewThreshold?: number;
  /**
   * This adds variant for the image
   */
  variant: VariantProps;
  /**
   * This adds divider
   */
  showDivider?: boolean;
  /**
   * This shows the bottom divider
   */
  showBottomDivider?: boolean;
  /**
   * Whether the title and chevron span the full width of the accordion or hug
   * together (the accordion as a whole will always take up the full width of
   * its container)
   */
  isFullWidth?: boolean;
  /**
   * disabled
   */
  disabled?: boolean;
  /**
   * This adds size of the accordion
   */
  size: AccordionSize;
  /**
   * This changes direction of the accordion
   */
  orientation?: 'top' | 'bottom';
  /**
   * This moves the text right of the accordion
   */
  titleAlign?: 'left' | 'right';
  /**
   * If true, the accordian is open by default
   */
  defaultOpen?: boolean;
  /**
   * The list of content items to display. Each item has a title (always
   * visible) and a description (can be expanded or collapsed).
   */
  items: Array<AccordionItem>;
} & WithMediaProps<{
    /**
     * This adds size of the accordion
     */
    size: AccordionSize;
  }>;

export type AccordionVariantProps = {
  /**
   * This adds variant for the image
   */
  variant: VariantProps;
};

/**
 * This component renders a list of collapsible content items.
 *
 * Key points to note:
 * - All content is passed via the `items` prop. Each item has a "title" that is
 *   always visible and a "description" that is visible when the item is
 *   expanded. If you only have one item of content, pass one object (with both
 *   a title and a description) to the `items` prop.
 * - Each item's `description` is usually a string, but you can pass JSX to
 *   render more complex content.
 */
export const Accordion = forwardRef(function Accordion(
  {
    analyticsName,
    trackImpression = false,
    inViewThreshold,
    showDivider,
    showBottomDivider = true,
    isFullWidth,
    variant = 'regular',
    disabled,
    items,
    titleAlign = 'left',
    orientation = 'top',
    defaultOpen = false,
    ...propsIn
  }: AccordionProps,
  ref: Ref<TamaguiElement>,
) {
  const { size = 'medium' } = useProps(propsIn);

  let singleValue;
  let multipleValue;
  if (defaultOpen) {
    if (items.length > 1) {
      multipleValue = items.map((item) => item.title);
    } else if (items.length > 0) {
      singleValue = items[0].title;
    }
  }

  const accordionItems = items?.map((accordionItem, idx) => {
    return (
      <AccordionItem
        showDivider={idx !== items.length - 1 ? showDivider : showDivider && showBottomDivider}
        isFullWidth={isFullWidth}
        disabled={disabled}
        variant={variant}
        size={size}
        title={accordionItem.title}
        description={accordionItem.description}
        key={accordionItem.title}
        titleAlign={titleAlign}
        orientation={orientation}
        analyticsName={analyticsName}
        suffix={accordionItem.suffix}
        eyebrowText={accordionItem.eyebrowText}
        defaultOpen={defaultOpen}
      />
    );
  });

  const accordion =
    accordionItems.length > 1 ? (
      <AccordionTamagui overflow="hidden" defaultValue={multipleValue} type={'multiple'}>
        {accordionItems}
      </AccordionTamagui>
    ) : (
      <AccordionTamagui overflow="hidden" defaultValue={singleValue} type={'single'} collapsible>
        {accordionItems}
      </AccordionTamagui>
    );

  if (analyticsName && trackImpression) {
    return (
      <ComponentWithViewTracking
        Component={accordion}
        analyticsName={analyticsName}
        eventName={'accordionViewEvent'}
        inViewThreshold={inViewThreshold}
        ref={ref}
      />
    );
  }

  return accordion;
});

export type AccordionItemProps = AccordionItem & {
  /**
   * showDivider
   */
  showDivider?: boolean;
  /**
   * isFullWidth
   */
  isFullWidth?: boolean;
  /**
   * disabled
   */
  disabled?: boolean;
  /**
   * variant
   */
  variant: VariantProps;
  /**
   * size
   */
  size: AccordionSize;
  /**
   * Align Title
   */
  titleAlign: 'left' | 'right';
  /**
   * Orientation
   */
  orientation: 'top' | 'bottom';
  /**
   * If true, accordion item is open by default
   */
  defaultOpen?: boolean;
  /**
   * Name of the accordionItem in analytics events, should be unique within the app
   * Convention: {appName}-{accordionName}
   * Accordion click events are tracked on the AccordionItem component
   * Accordion list view events are tracked on the Accordion component
   */
  analyticsName?: AnalyticsProps['analyticsName'];
};

const AccordionItem = ({
  showDivider,
  isFullWidth,
  disabled,
  title,
  description,
  size,
  variant,
  titleAlign,
  orientation,
  analyticsName,
  suffix,
  eyebrowText,
  defaultOpen,
}: AccordionItemProps) => {
  const showSuffix = !isFullWidth && suffix;
  const { trackJourneyEvent } = useObservabilitySafe();
  const [isOpen, setIsOpen] = useState(defaultOpen ?? false);

  let triggerColor: Color = '$contentPrimary';
  if (disabled) {
    triggerColor = accordionTokens.color.content.state.inactive;
  }

  const handlePress = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
    // Track accordion click event for analytics
    if (analyticsName) {
      const data = { targetName: analyticsName, accordionName: title };
      trackJourneyEvent('platform', 'accordionClickEvent', data);
    }
  };

  return (
    <AccordionTamagui.Item
      value={title}
      data-testid="accordionTamagui-item"
      flexDirection={orientation === 'top' ? 'column' : 'column-reverse'}
      borderBottomWidth={showDivider ? '$default' : 0}
      borderBottomColor={showDivider ? '$borderPrimary' : 'unset'}
    >
      <AccordionTamagui.Trigger
        height={accordionTokens.size.height[size]}
        gap={accordionTokens.padding.betweenEyebrowAndValueText[size]}
        justifyContent="center"
        backgroundColor="$transparent"
        borderWidth={0}
        onPress={handlePress}
        focusVisibleStyle={{
          // Make sure all of the outline is visible, even with overflow="hidden"
          outlineOffset: -1,
        }}
        paddingHorizontal={0}
        disabled={disabled}
        pointerEvents={disabled ? 'none' : 'auto'}
      >
        {eyebrowText && (
          <Box alignItems={titleAlign === 'right' ? 'flex-end' : 'flex-start'} paddingRight="$12x">
            <Text
              tag="span"
              typography={accordionTokens.typography.eyebrow.default}
              fontWeight={accordionTokens.fontWeight.eyebrowOverride}
              color={accordionTokens.color.content.eyebrow}
            >
              {eyebrowText}
            </Text>
          </Box>
        )}
        <Box
          flexDirection="row"
          alignItems="center"
          justifyContent={
            isFullWidth ? 'space-between' : titleAlign === 'right' ? 'flex-end' : 'flex-start'
          }
          columnGap={accordionTokens.padding.betweenTextChevron.default}
        >
          <Text
            tag="span"
            typography={accordionTokens.typography[size][variant]}
            fontWeight={
              variant === 'regular' ? accordionTokens.fontWeight.valueTextOverride : undefined
            }
            color={triggerColor}
          >
            {title}
          </Text>
          <Box rotate={isOpen ? '180deg' : '0deg'} animation="accelerateDecelerateFastest">
            <ChevronDown size={20} color={triggerColor} />
          </Box>
          {showSuffix && <Box flexGrow={1}>{suffix}</Box>}
        </Box>
      </AccordionTamagui.Trigger>

      <HeightAnimator isOpen={isOpen}>
        <AccordionTamagui.Content
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
          animation="accelerateDecelerateFastest"
          paddingBottom={accordionTokens.padding.betweenContentAndDivider[size]}
        >
          {typeof description === 'string' ? (
            <Text tag="p" typography="$bodyMedium">
              {description}
            </Text>
          ) : (
            description
          )}
        </AccordionTamagui.Content>
      </HeightAnimator>
    </AccordionTamagui.Item>
  );
};
